import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const empty = { name: "", email: "", company: "", job_title: "", phone: "", interest: "", message: "" };

const LeadDialog = ({
  trigger,
  title = "Request Access",
  description = "Complete the form to continue. Our team will follow up with relevant insights.",
  source = "lead-generation",
  reportSlug = null,
  onGranted,
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Name, work email and company are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { ...form, source, report_slug: reportSlug });
      toast.success("Thank you! Your request has been received.");
      setForm(empty);
      setOpen(false);
      if (onGranted) onGranted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ld-name">Full name *</Label>
              <Input id="ld-name" value={form.name} onChange={set("name")} data-testid="lead-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ld-company">Company *</Label>
              <Input id="ld-company" value={form.company} onChange={set("company")} data-testid="lead-company" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ld-email">Work email *</Label>
            <Input id="ld-email" type="email" value={form.email} onChange={set("email")} data-testid="lead-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ld-title">Job title</Label>
              <Input id="ld-title" value={form.job_title} onChange={set("job_title")} data-testid="lead-title" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ld-phone">Phone</Label>
              <Input id="ld-phone" value={form.phone} onChange={set("phone")} data-testid="lead-phone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ld-msg">What are you looking for?</Label>
            <Textarea id="ld-msg" rows={3} value={form.message} onChange={set("message")} data-testid="lead-message" />
          </div>
          <Button type="submit" disabled={loading} className="btn-electric w-full rounded-full" data-testid="lead-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            By submitting you agree to be contacted by Lead ITES about relevant insights.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadDialog;
